import { useEffect, useState } from "react";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import { Link } from "react-router-dom";
import { ShieldCheck, CalendarClock, RefreshCw, HardHat, AlertTriangle } from "lucide-react";

const STATUS_STYLE = {
  overdue: "bg-[#b91c1c]/10 text-[#b91c1c] border-[#b91c1c]/30",
  due_soon: "bg-[#E65100]/10 text-[#E65100] border-[#E65100]/30",
  ok: "bg-[#166534]/10 text-[#166534] border-[#166534]/30",
};

function dueLabel(days) {
  if (days === null || days === undefined) return "";
  if (days < 0) return `Overdue by ${-days}d`;
  if (days === 0) return "Due today";
  return `Due in ${days}d`;
}

function DateRow({ r }) {
  return (
    <tr data-testid={`compliance-row-${r.card_id}`} className="border-b border-[#15233B]/8">
      <td className="py-2 pr-2 font-bold text-[#15233B]">{r.full_name} <span className="font-mono text-[10px] text-[#15233B]/45">{r.av_id}</span></td>
      {r.payer_mco !== undefined && <td className="py-2 px-2 text-xs text-[#15233B]/70">{r.payer_mco}</td>}
      <td className="py-2 px-2 tabular-nums text-[#15233B]/80">{r.due_on}</td>
      <td className="py-2 pl-2 text-right">
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${STATUS_STYLE[r.status] || ""}`}>{dueLabel(r.days)}</span>
      </td>
    </tr>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div className="bg-white border border-[#15233B]/10 rounded-md p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-[#FBBF24]" />
        <h3 className="text-sm font-extrabold tracking-tight text-[#15233B] uppercase tracking-[0.15em]">{title}</h3>
        {count > 0 && <span className="text-[10px] font-bold bg-[#15233B] text-white px-1.5 py-0.5 rounded">{count}</span>}
      </div>
      {children}
    </div>
  );
}

export default function CompliancePage() {
  const [data, setData] = useState({ supervisory: [], recert: [], pcws_incomplete: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/compliance").then(({ data }) => { setData(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const overdueCount = [...data.supervisory, ...data.recert].filter((x) => x.status === "overdue").length;
  const soonCount = [...data.supervisory, ...data.recert].filter((x) => x.status === "due_soon").length;

  return (
    <div className="min-h-screen flex flex-col onboarding-bg">
      <Header />
      <div aria-hidden className="fixed inset-0 z-0 pointer-events-none bg-no-repeat" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/brand/avida-logo.png)`, backgroundPosition: "center 52%", backgroundSize: "min(640px, 72vw)", opacity: 0.05 }} />
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 max-w-5xl w-full mx-auto space-y-5">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#FBBF24]" />
            <h1 className="text-2xl font-extrabold tracking-tight text-[#15233B]">Compliance</h1>
          </div>
          <p className="text-sm text-[#15233B]/60 mt-1">Supervisory visits, recertifications, and PCW onboarding that need attention.</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div data-testid="compliance-overdue" className="bg-white border border-[#b91c1c]/20 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#b91c1c]">Overdue</div>
            <div className="text-3xl font-extrabold text-[#b91c1c] mt-1">{overdueCount}</div>
          </div>
          <div data-testid="compliance-soon" className="bg-white border border-[#E65100]/20 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E65100]">Due within 14d</div>
            <div className="text-3xl font-extrabold text-[#E65100] mt-1">{soonCount}</div>
          </div>
          <div data-testid="compliance-pcws" className="bg-white border border-[#15233B]/10 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">PCWs pending</div>
            <div className="text-3xl font-extrabold text-[#15233B] mt-1">{data.pcws_incomplete.length}</div>
          </div>
        </div>

        {loading ? <div className="text-sm text-[#15233B]/50">Loading…</div> : (
          <>
            <Section title="50–60 day supervisory visits" icon={CalendarClock} count={data.supervisory.length}>
              {data.supervisory.length === 0 ? (
                <div className="text-xs italic text-[#15233B]/40 py-3">No supervisory visits scheduled. Set a due date in a client's Onboarding section.</div>
              ) : (
                <table className="w-full text-xs"><tbody>{data.supervisory.map((r) => <DateRow key={r.card_id} r={r} />)}</tbody></table>
              )}
            </Section>

            <Section title="Recertifications" icon={RefreshCw} count={data.recert.length}>
              {data.recert.length === 0 ? (
                <div className="text-xs italic text-[#15233B]/40 py-3">No recertifications scheduled. Set a recert date in a client's Onboarding section.</div>
              ) : (
                <table className="w-full text-xs"><tbody>{data.recert.map((r) => <DateRow key={r.card_id} r={r} />)}</tbody></table>
              )}
            </Section>

            <Section title="PCWs with incomplete onboarding" icon={HardHat} count={data.pcws_incomplete.length}>
              {data.pcws_incomplete.length === 0 ? (
                <div className="text-xs italic text-[#15233B]/40 py-3">All active PCWs are fully onboarded. 🎉</div>
              ) : (
                <div className="space-y-2">
                  {data.pcws_incomplete.map((p) => (
                    <div key={p.id} data-testid={`compliance-pcw-${p.id}`} className="flex items-start gap-2 text-sm border-b border-[#15233B]/8 pb-2">
                      <AlertTriangle className="h-4 w-4 text-[#E65100] mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <span className="font-bold text-[#15233B]">{p.name}</span>{" "}
                        <span className="text-[11px] text-[#15233B]/55">{p.done}/{p.total} complete</span>
                        <div className="text-[11px] text-[#15233B]/60 mt-0.5">Missing: {p.missing.join(" · ")}</div>
                      </div>
                    </div>
                  ))}
                  <Link to="/pcws" className="inline-block text-xs font-semibold text-[#FBBF24] hover:underline mt-1">Manage PCWs →</Link>
                </div>
              )}
            </Section>
          </>
        )}
      </div>
      <BrandFooter />
    </div>
  );
}
